import { Filter, FilterConfig, FilterTypes, IsFilterable } from "@/lib/filterable";
import { ICreateDto } from "@/lib/utility/data/create-dto.interface";
import reportGroupService from "@/services/mrfiktiv/services/report-group.service";
import {
  MrfiktivReportGroupViewModelGen,
  MrfiktivCreateReportGroupDtoGen,
  MrfiktivCustomFieldListElementDtoGen,
  MrfiktivUpdateReportGroupDtoGen
} from "@/services/mrfiktiv/v1/data-contracts";
import { ReportGroupDataAccessLayer } from "@/store/modules/access-layers/report-group.access-layer";
import { ITimestamp, Timestamp } from "./timestamp.entity";
import { IReportGroupConfiguration, ReportGroupConfiguration } from "./report-group-configuration.entity";
import { PageFilterTypes } from "@/lib/utility/data/page-filter-types.enum";
import { CustomFieldEnum } from "@/store/modules/custom-field.store";
import { $t } from "@/lib/utility/t";
import { ReportGoToHelper } from "@/lib/utility/report.go-to-helper";
import VueRouter from "vue-router";

@IsFilterable
export class ReportGroupBase implements MrfiktivReportGroupViewModelGen, ICreateDto<IReportGroup> {
  /**
   * @inheritdoc
   */
  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "id",
    config: {
      itemCallback: () => ReportGroupDataAccessLayer.entities,
      mapItemToComponent: (item) => ({ item }),
      itemValue: "id",
      component: "refs-report-group"
    }
  })
  id: string;

  partnerId: string;

  configuration: IReportGroupConfiguration;

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.reportGroup.title"
  })
  title: string;

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.reportGroup.description"
  })
  description?: string | undefined;

  @FilterConfig({ type: PageFilterTypes.BOOLEAN, displayName: "objects.reportGroup.isDefault" })
  isDefault?: boolean;

  @FilterConfig({
    type: Timestamp
  })
  timestamp: ITimestamp;

  static get statusCustomFieldTypes(): CustomFieldEnum[] {
    return [CustomFieldEnum.SINGLE_SELECT];
  }

  private get customFieldConfigDto(): MrfiktivCustomFieldListElementDtoGen[] {
    return this.configuration.customFieldConfig
      .filter((c) => c.customField.id) // filter out deleted custom fields
      .map((c) => {
        return {
          configuration: c.configuration,
          customField: c.customField.id
        };
      });
  }

  loading = false;

  isLoadingDefault = false;

  /**
   * Construct reportGroup
   */
  constructor(reportGroup?: Partial<IReportGroup | MrfiktivReportGroupViewModelGen>) {
    this.id = reportGroup?.id ?? "";
    this.partnerId = reportGroup?.partnerId ?? "";
    this.configuration = new ReportGroupConfiguration(reportGroup?.configuration);
    this.title = reportGroup?.title ?? "";
    this.description = reportGroup?.description ?? "";
    this.isDefault = reportGroup?.isDefault ?? false;
    this.timestamp = new Timestamp(reportGroup?.timestamp);
  }

  /**
   * map props from viewmodel to this
   */
  protected map(reportGroup?: MrfiktivReportGroupViewModelGen) {
    if (!reportGroup) return;
    this.id = reportGroup?.id ?? "";
    this.partnerId = reportGroup?.partnerId ?? "";
    this.configuration = new ReportGroupConfiguration(reportGroup?.configuration);
    this.title = reportGroup?.title ?? "";
    this.description = reportGroup?.description ?? "";
    this.isDefault = reportGroup?.isDefault ?? false;
    this.timestamp = new Timestamp(reportGroup?.timestamp);
  }

  /**
   * fetch reportGroup
   */
  async fetch(): Promise<this> {
    try {
      this.loading = true;
      this.isLoadingDefault = true;
      const res = await reportGroupService.getOne(this.partnerId, this.id);

      this.map(res);
      ReportGroupDataAccessLayer.set(this);

      this.loading = false;

      return this;
    } finally {
      this.loading = false;
      this.isLoadingDefault = false;
    }
  }

  /**
   * create fetch reportGroup
   */
  async create() {
    const data: MrfiktivCreateReportGroupDtoGen = {
      title: this.title,
      description: this.description || undefined,
      configuration: this.configuration?.dto,
      isDefault: this.isDefault
    };
    const res = await reportGroupService.create(this.partnerId, data);

    this.map(res);

    ReportGroupDataAccessLayer.set(this);

    if (this.isDefault) {
      await this.fetchOtherLocalDefault();
    }

    return this;
  }

  /**
   * delete reportGroup
   */
  async delete() {
    if (this.isDefault) {
      throw new Error($t("report.deleteDefaultHint"));
    }
    const res = await reportGroupService.delete(this.partnerId, this.id);

    this.map(res);
    ReportGroupDataAccessLayer.delete(this);
  }

  /**
   * update reportGroup
   * @returns
   */
  async update() {
    const data: MrfiktivUpdateReportGroupDtoGen = {
      title: this.title,
      description: this.description || undefined,
      configuration: this.configuration?.dto,
      isDefault: this.isDefault
    };

    return this.updatePartial(data);
  }

  /**
   * update reportGroup via dto
   * @param dto
   * @returns
   */
  async updatePartial(dto: MrfiktivUpdateReportGroupDtoGen) {
    const res = await reportGroupService.update(this.partnerId, this.id, dto);
    if (dto.isDefault) {
      this.fetchOtherLocalDefault();
    }

    this.map(res);

    ReportGroupDataAccessLayer.set(this);

    return this;
  }

  /**
   * Updates the default status of this report group.
   * If setting as default, ensures no other group is marked as default.
   */
  async updateDefault(isDefault: boolean) {
    try {
      this.isLoadingDefault = true;
      await this.updatePartial({ isDefault });
    } finally {
      this.isLoadingDefault = false;
    }

    return this;
  }

  /**
   * There can only be one default, so the backend ensures that other defaults are unselected as default.
   * If we have that instance in the frontend we can update it with this helper
   */
  async fetchOtherLocalDefault() {
    await ReportGroupDataAccessLayer.entities.find((g) => g.isDefault && g.id !== this.id)?.fetch();
  }

  goTo(router: VueRouter) {
    return {
      table: (newTab = false) =>
        new ReportGoToHelper(router).goToReportGroupTable({
          partnerId: this.partnerId,
          newTab
        }),
      detail: (newTab = false) =>
        new ReportGoToHelper(router).goToReportGroupDetail({
          partnerId: this.partnerId,
          reportGroupId: this.id,
          newTab
        }),
      customView: (newTab = false) =>
        new ReportGoToHelper(router).goToReportGroupCustomView({
          partnerId: this.partnerId,
          reportGroupId: this.id,
          viewId: "0",
          newTab
        })
    };
  }
}

type IReportGroup = ReportGroupBase;
const ReportGroup = Filter.createForClass(ReportGroupBase);

export { ReportGroup, IReportGroup };
